import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import {
  Shield, Mail, Loader2, X, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

/**
 * VerifyParentalConsentModal — Iter76 / P2.
 *
 * Replaces the older "send a parental email and wait" flow with a
 * proper provider chooser:
 *   - DigiLocker (India)  — real OAuth when env keys exist, else MOCK
 *   - ID.me (United States) — same
 *   - Parent email + legal-guardian attestation (universal fallback)
 *
 * For the MOCK path we collect parent name, relationship, and a
 * required legal-guardian attestation checkbox. The backend writes an
 * audit row in `vpc_audit` so compliance reviewers can trace every
 * minor account back to a named guardian.
 *
 * Real OAuth: when DIGILOCKER_CLIENT_ID / IDME_CLIENT_ID are set on
 * the backend, the API returns a `start_url` we open in a popup. The
 * existing `/parental-consent/confirm/{token}` callback completes the
 * flow. Today (Feb 2026) neither provider is provisioned — clearly
 * surfaced as "MOCK" in the UI so a parent never thinks they did a
 * real government ID check when they didn't.
 */
const ICONS = { shield: Shield, mail: Mail };
const RELATIONSHIPS = [
  { id: "parent", label: "Parent" },
  { id: "legal_guardian", label: "Legal guardian" },
  { id: "mother", label: "Mother" },
  { id: "father", label: "Father" },
];

export default function VerifyParentalConsentModal({ open, onOpenChange, onConfirmed }) {
  const [providers, setProviders] = useState(null);
  const [selected, setSelected] = useState(null);
  const [parentName, setParentName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [attest, setAttest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get("/parental-consent/providers")
      .then(({ data }) => {
        if (cancelled) return;
        setProviders(data);
        // Auto-select region-appropriate provider.
        const region = data?.region;
        const list = data?.providers || [];
        const preferred = list.find((p) => p.available_for_region && p.id !== "email_attestation")
          || list.find((p) => p.available_for_region);
        if (preferred) setSelected(preferred.id);
      })
      .catch(() => toast.error("Couldn't load consent providers"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const selectedProvider = (providers?.providers || []).find((p) => p.id === selected);
  const realOAuth = !!selectedProvider?.real_oauth;

  const submit = async () => {
    if (!selectedProvider) return;
    if (!realOAuth) {
      if (!parentName.trim()) { toast.error("Parent name required"); return; }
      if (!relationship) { toast.error("Pick the relationship"); return; }
      if (!attest) { toast.error("Tick the legal-guardian attestation"); return; }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/parental-consent/verify", {
        provider: selectedProvider.id,
        parent_name: parentName,
        relationship,
        attest_legal_guardian: attest,
      });
      if (data?.mode === "real_oauth" && data?.start_url) {
        // Real-OAuth path: open the start URL in a popup. Parent
        // completes verification on the provider's domain, we'll
        // pick it up via the existing confirm/{token} callback.
        window.open(data.start_url, "oak-vpc", "width=520,height=720");
        toast.message("Complete verification in the popup window.");
      } else {
        toast.success("Parental consent recorded.");
        onConfirmed?.();
        onOpenChange?.(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0B0E14]/95 border-white/10 text-white max-w-md p-0 overflow-hidden"
        data-testid="vpc-modal"
      >
        <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 grid place-items-center rounded-lg bg-[#0044FF]/12 border border-[#0044FF]/35">
              <Shield size={15} className="text-[#9bc7ff]" />
            </div>
            <div>
              <div className="font-display text-sm tracking-tight">Parental consent</div>
              <div className="text-[10.5px] text-white/45 font-mono-oak">
                One-time verification for minor accounts
              </div>
            </div>
          </div>
          <button
            onClick={() => onOpenChange?.(false)}
            className="h-7 w-7 grid place-items-center rounded-md text-white/45 hover:text-white hover:bg-white/5"
            aria-label="Close"
            data-testid="vpc-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="grid place-items-center py-10 text-white/55 text-sm">
              <Loader2 size={16} className="animate-spin mb-2" /> Loading…
            </div>
          )}

          {!loading && providers?.parental_consent && (
            <div className="flex items-start gap-2.5 text-[12.5px] text-[#9bffcc] bg-[#00FF9D]/[0.05] border border-[#00FF9D]/22 rounded-lg p-3">
              <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
              <div>
                Already verified on {providers.parental_consent_at?.slice(0, 10) || "—"}
                {providers.vpc_provider ? ` via ${providers.vpc_provider}` : ""}. Your child has full account access.
              </div>
            </div>
          )}

          {!loading && providers && (
            <>
              <div className="text-[12.5px] text-white/65 leading-relaxed">
                We need a verifiable adult to consent before a learner under 18 can use the full platform. Pick the most convenient option for your country.
              </div>

              <div className="space-y-2">
                {(providers.providers || [])
                  .filter((p) => p.available_for_region)
                  .map((p) => {
                    const Icon = ICONS[p.logo] || Shield;
                    const isSel = selected === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelected(p.id)}
                        className={`w-full text-left px-3 py-3 rounded-xl border transition flex items-start gap-3 ${
                          isSel
                            ? "border-[#0044FF]/55 bg-[#0044FF]/12 text-white"
                            : "border-white/8 bg-white/[0.025] text-white/85 hover:bg-white/[0.05] hover:border-white/20"
                        }`}
                        data-testid={`vpc-provider-${p.id}`}
                      >
                        <div className={`h-9 w-9 grid place-items-center rounded-lg shrink-0 border ${
                          isSel ? "border-[#0044FF]/55 bg-[#0044FF]/15" : "border-white/10 bg-white/5"
                        }`}>
                          <Icon size={14} className={isSel ? "text-[#9bc7ff]" : "text-white/65"} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-[13px] flex items-center gap-2 flex-wrap">
                            {p.label}
                            {!p.real_oauth && (
                              <span className="text-[9px] uppercase tracking-[0.18em] font-mono-oak text-amber-300 bg-amber-500/10 border border-amber-400/22 px-1.5 py-0.5 rounded">
                                attestation
                              </span>
                            )}
                            {p.real_oauth && (
                              <span className="text-[9px] uppercase tracking-[0.18em] font-mono-oak text-[#9bffcc] bg-[#00FF9D]/10 border border-[#00FF9D]/22 px-1.5 py-0.5 rounded">
                                verified
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-white/50 mt-0.5">
                            {p.real_oauth
                              ? "Full government-ID verification via OAuth."
                              : "Parent attests in good faith — recorded for compliance audit."}
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </div>

              {selectedProvider && !realOAuth && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3 mt-2 pt-3 border-t border-white/8"
                >
                  <div className="flex items-start gap-2 text-[11px] text-amber-200 bg-amber-500/8 border border-amber-400/22 rounded-lg p-2.5">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    <div>
                      This option is a parent-attested consent. Real DigiLocker / ID.me OAuth will activate automatically once licensing is provisioned.
                    </div>
                  </div>
                  <label className="block">
                    <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-display mb-1">Parent / guardian full name</div>
                    <input
                      type="text"
                      value={parentName}
                      onChange={(e) => setParentName(e.target.value.slice(0, 80))}
                      placeholder="e.g. Priya Sharma"
                      className="w-full text-[13px] px-3 py-2 rounded-lg border border-white/10 bg-white/[0.04] text-white placeholder:text-white/35 focus:outline-none focus:border-white/30"
                      data-testid="vpc-parent-name"
                    />
                  </label>
                  <div>
                    <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-display mb-1">Relationship to learner</div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {RELATIONSHIPS.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => setRelationship(r.id)}
                          className={`text-[11.5px] px-3 py-1.5 rounded-lg border transition ${
                            relationship === r.id
                              ? "border-[#0044FF]/55 bg-[#0044FF]/12 text-white"
                              : "border-white/10 bg-white/[0.03] text-white/75 hover:bg-white/[0.06]"
                          }`}
                          data-testid={`vpc-relationship-${r.id}`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-start gap-2.5 text-[11.5px] text-white/75 leading-snug cursor-pointer">
                    <input
                      type="checkbox"
                      checked={attest}
                      onChange={(e) => setAttest(e.target.checked)}
                      className="mt-0.5 accent-[#0044FF]"
                      data-testid="vpc-attest"
                    />
                    <span>
                      I confirm that I am the parent or legal guardian of this learner and consent to their use of Oak AI Campus's features under applicable child-data laws (DPDPA / COPPA / GDPR-K).
                    </span>
                  </label>
                </motion.div>
              )}
            </>
          )}
        </div>

        {!loading && providers && (
          <div className="px-5 py-4 border-t border-white/8 flex items-center justify-between gap-3">
            <div className="text-[10.5px] text-white/40 font-mono-oak">
              Region detected: <span className="text-white/65">{providers.region}</span>
            </div>
            <button
              onClick={submit}
              disabled={submitting || !selectedProvider || (!realOAuth && !(parentName && relationship && attest))}
              className="btn-primary px-5 py-2 rounded-full font-display font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              data-testid="vpc-submit"
            >
              {submitting
                ? <><Loader2 size={12} className="animate-spin" /> Verifying…</>
                : realOAuth ? "Start OAuth" : "Confirm consent"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
